import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Pencil,
  Power,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import {
  type AlertDefinition,
  ExecuteCooldownError,
  useAlertDefinition,
  useDeleteAlertDefinition,
  useDisableAlertDefinition,
  useEnableAlertDefinition,
  useExecuteAlertDefinition,
} from '@/hooks/useAlertDefinitions';
import { AlertIncidentDisposition, type DispositionAction } from '@/components/alerts/AlertIncidentDisposition';
import { AlertFormModal } from '@/components/alerts/AlertFormModal';
import { ExecutionHistory } from '@/components/alerts/ExecutionHistory';
import { OverridesPanel } from '@/components/alerts/OverridesPanel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/ui/page-container';
import { cn } from '@/lib/utils';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';

/**
 * AlertDetails — single alert-rule page. Spec §4 MF (oncall companion).
 *
 * Layout: 2/3 main column (description / condition+window / actions /
 * execution history / overrides) + 1/3 sidecar (schedule + run state).
 *
 * Actions in header: Run-now, Edit (opens AlertFormModal — still Bulma),
 * Enable/Disable toggle, Delete (when not system_default).
 *
 * The four embedded panels (ExecutionHistory, OverridesPanel,
 * AlertFormModal, plus the inline summary tables) stay Bulma for now —
 * they're shared with /alerts and migrate together later.
 */

const STATE_STYLE: Record<string, string> = {
  alerting: 'bg-sev-1/15 text-sev-1',
  pending: 'bg-sev-3/20 text-foreground',
  ok: 'bg-sev-4/15 text-sev-4',
};

const TASK_TYPE_LABEL: Record<string, string> = {
  metric_alert: 'Metric alert',
  device_audit: 'Device audit',
  custom_analysis: 'Analysis',
};

function formatInterval(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m`;
  if (seconds < 86400) return `${String(Math.floor(seconds / 3600))}h`;
  return `${String(Math.floor(seconds / 86400))}d`;
}

// ─── Inline summary tables ────────────────────────────────────────────

function ConditionSummary({ condition }: { condition: Record<string, unknown> }) {
  const type = (condition.type as string) || 'threshold';
  const metric = (condition.metric as string) || '—';
  const operator = condition.operator as string | undefined;
  const value = condition.value as number | string | undefined;
  const refMetric = condition.reference_metric as string | undefined;
  const transform = condition.transform as string | undefined;
  const silenceMinutes = condition.silence_minutes as number | undefined;
  const overMinutes = condition.over_minutes as number | undefined;
  const unit = condition.unit as string | undefined;

  return (
    <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
      <SummaryRow label="Type" value={type.replace('_', ' ')} />
      <SummaryRow label="Metric" value={<code className="font-mono text-xs">{metric}</code>} />
      {operator !== undefined && value !== undefined ? (
        <SummaryRow
          label="Threshold"
          value={<code className="font-mono text-xs">{operator} {String(value)}</code>}
        />
      ) : null}
      {refMetric ? (
        <SummaryRow label="Reference" value={<code className="font-mono text-xs">{refMetric}</code>} />
      ) : null}
      {transform ? (
        <SummaryRow label="Transform" value={<code className="font-mono text-xs">{transform}</code>} />
      ) : null}
      {silenceMinutes !== undefined ? <SummaryRow label="Silence" value={`${String(silenceMinutes)}m`} /> : null}
      {overMinutes !== undefined ? <SummaryRow label="Over" value={`${String(overMinutes)}m`} /> : null}
      {unit ? <SummaryRow label="Unit" value={unit} /> : null}
    </dl>
  );
}

function WindowSummary({ window }: { window: Record<string, unknown> }) {
  const type = (window.type as string) || 'sustained';
  const windowMinutes = window.window_minutes as number | undefined;
  const consecutiveMinutes = window.consecutive_minutes as number | undefined;
  const params = window.params as Record<string, unknown> | undefined;

  return (
    <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
      <SummaryRow label="Strategy" value={type.replace('_', ' ')} />
      {windowMinutes !== undefined ? <SummaryRow label="Window" value={`${String(windowMinutes)}m`} /> : null}
      {consecutiveMinutes !== undefined ? (
        <SummaryRow label="Consecutive" value={`${String(consecutiveMinutes)}m`} />
      ) : null}
      {typeof params?.min_datapoints === 'number' ? (
        <SummaryRow label="Min datapoints" value={String(params.min_datapoints)} />
      ) : null}
      {typeof params?.m === 'number' && typeof params.n === 'number' ? (
        <SummaryRow label="M-of-N" value={`${String(params.m)} of ${String(params.n)}`} />
      ) : null}
    </dl>
  );
}

function ActionsSummary({ actions }: { actions: Record<string, unknown>[] }) {
  if (actions.length === 0) {
    return <p className="text-sm text-muted-foreground">No actions configured.</p>;
  }
  return (
    <ul className="space-y-2">
      {actions.map((action, i) => {
        const type = typeof action.type === 'string' ? action.type : 'unknown';
        const str = (key: string): string => (typeof action[key] === 'string' ? action[key] : '');
        let detail = '';
        // Only the event-shaping actions have a detail worth rendering. The channel
        // actions that used to appear here are gone (§4) — delivery is routing policy
        // now — and an unrecognised type still renders its badge rather than vanishing,
        // so a row restored from an old dump is visible rather than silently omitted.
        if (type === 'create_incident') {
          detail = ` — title: "${str('title_template')}"`;
        }
        return (
          <li key={i} className="text-sm flex items-start gap-2">
            <Badge variant="secondary" className="text-[10px] font-normal capitalize flex-shrink-0">
              {type.replace(/_/g, ' ')}
            </Badge>
            <span className="text-muted-foreground">{detail}</span>
          </li>
        );
      })}
    </ul>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground font-medium">{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

export default function AlertDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditOpen, setIsEditOpen] = useState(false);
  /// Which decision the user is being asked to make, if any.
  const [disposition, setDisposition] = useState<DispositionAction | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning' | 'error'; text: string } | null>(null);

  const { data: task, isLoading, isError } = useAlertDefinition(id);
  const executeMutation = useExecuteAlertDefinition();
  const enableMutation = useEnableAlertDefinition();
  const disableMutation = useDisableAlertDefinition();
  const deleteMutation = useDeleteAlertDefinition();

  const handleRun = async () => {
    if (!task) return;
    setNotice(null);
    try {
      await executeMutation.mutateAsync(task.id);
      setNotice({ kind: 'success', text: 'Alert queued for immediate re-evaluation.' });
    } catch (err) {
      if (err instanceof ExecuteCooldownError) {
        setNotice({ kind: 'warning', text: err.message });
      } else {
        setNotice({ kind: 'error', text: 'Failed to re-run alert.' });
      }
    }
  };

  const handleToggleEnabled = () => {
    if (!task) return;
    if (task.enabled) {
      // Pausing may strand open incidents, so it asks rather than acts.
      setDisposition('pause');
    } else {
      enableMutation.mutate(task.id);
    }
  };

  const handleDelete = () => {
    if (!task) return;
    setDisposition('delete');
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-9 w-80 mb-2" />
        <Skeleton className="h-4 w-32 mb-6" />
        <Skeleton className="h-32 w-full" />
      </PageContainer>
    );
  }

  if (isError || !task) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load alert.</AlertDescription>
        </Alert>
        <Button variant="outline" asChild>
          <Link to="/alerts">
            <ChevronLeft className="w-4 h-4" />
            Back to alerts
          </Link>
        </Button>
      </PageContainer>
    );
  }

  const t: AlertDefinition = task;
  const condition = (t.config.condition as Record<string, unknown> | undefined) ?? null;
  const windowConfig = (t.config.window as Record<string, unknown> | undefined) ?? null;
  const auditType = t.config.audit_type as string | undefined;
  const isSystemDefault = t.source === 'system_default';

  return (
    <PageContainer>
      <nav className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-3" aria-label="Breadcrumb">
        <Link to="/alerts" className="hover:text-foreground">Alerts</Link>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className="text-foreground truncate">{t.name}</span>
      </nav>
      <Link
        to="/alerts"
        className="md:hidden inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-3 -ml-1"
      >
        <ChevronLeft className="w-4 h-4" />
        All alerts
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-5">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight break-words">
            {t.name}
          </h1>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <SeverityBadge severity={t.severity} />
            <Badge
              className={cn(
                'border-0 font-normal capitalize',
                STATE_STYLE[t.alertState] ?? 'bg-muted text-muted-foreground',
              )}
            >
              {t.alertState}
            </Badge>
            <Badge
              className={cn(
                'border-0 font-normal',
                t.enabled
                  ? 'bg-sev-4/15 text-sev-4'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {t.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Badge variant="secondary" className="font-normal">
              {TASK_TYPE_LABEL[t.taskType] ?? t.taskType}
            </Badge>
            {isSystemDefault ? <Badge variant="outline" className="text-[10px]">Default</Badge> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void handleRun(); }}
            disabled={executeMutation.isPending}
            title="Queue this alert for immediate re-evaluation (limited to once per minute)"
          >
            <RefreshCw className={cn('w-4 h-4', executeMutation.isPending && 'animate-spin')} />
            Run now
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setIsEditOpen(true); }}>
            <Pencil className="w-4 h-4" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleEnabled}
            disabled={enableMutation.isPending || disableMutation.isPending}
          >
            <Power className="w-4 h-4" />
            {t.enabled ? 'Disable' : 'Enable'}
          </Button>
          {!isSystemDefault ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { handleDelete(); }}
              disabled={deleteMutation.isPending}
              className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      {/* Run-now notice */}
      {notice ? (
        <Alert
          variant={notice.kind === 'error' ? 'destructive' : 'default'}
          className={cn(
            'mb-4 relative',
            notice.kind === 'warning' && 'border-sev-3 bg-sev-3-soft text-foreground',
            notice.kind === 'success' && 'border-sev-4 bg-sev-4/10 text-foreground',
          )}
        >
          {notice.kind === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{notice.text}</AlertDescription>
          <button
            type="button"
            onClick={() => { setNotice(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {/* 2/3 + 1/3 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Description</h2>
            {t.description ? (
              <p className="text-sm">{t.description}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No description.</p>
            )}
          </section>

          {t.taskType === 'metric_alert' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <section className="bg-card border border-border rounded-lg p-5">
                <h2 className="text-sm font-semibold mb-3">Condition</h2>
                {condition ? (
                  <ConditionSummary condition={condition} />
                ) : (
                  <p className="text-sm text-muted-foreground">No condition configured.</p>
                )}
              </section>
              <section className="bg-card border border-border rounded-lg p-5">
                <h2 className="text-sm font-semibold mb-3">Window</h2>
                {windowConfig ? (
                  <WindowSummary window={windowConfig} />
                ) : (
                  <p className="text-sm text-muted-foreground">No window configured.</p>
                )}
              </section>
            </div>
          ) : null}

          {t.taskType === 'device_audit' ? (
            <section className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-3">Audit</h2>
              <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
                <SummaryRow
                  label="Type"
                  value={<code className="font-mono text-xs">{auditType ?? '—'}</code>}
                />
              </dl>
            </section>
          ) : null}

          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Actions</h2>
            <ActionsSummary actions={t.actions} />
          </section>

          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Execution history</h2>
            <ExecutionHistory taskId={t.id} />
          </section>

          <section className="bg-card border border-border rounded-lg p-5">
            <OverridesPanel taskId={t.id} />
          </section>
        </div>

        <aside className="space-y-4 md:space-y-5">
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Schedule</h2>
            <dl className="text-xs space-y-2">
              <ScheduleRow label="Interval" value={formatInterval(t.intervalSeconds)} />
              <ScheduleRow
                label="Last run"
                value={
                  t.lastRunAt
                    ? formatDistanceToNow(new Date(t.lastRunAt), { addSuffix: true })
                    : <span className="text-muted-foreground">Never</span>
                }
              />
              {t.lastResult ? (
                <ScheduleRow
                  label="Last result"
                  value={
                    <Badge
                      className={cn(
                        'border-0 font-normal',
                        t.lastResult === 'alert'
                          ? 'bg-sev-1/15 text-sev-1'
                          : t.lastResult === 'error'
                            ? 'bg-sev-3/20 text-foreground'
                            : 'bg-sev-4/15 text-sev-4',
                      )}
                    >
                      {t.lastResult}
                    </Badge>
                  }
                />
              ) : null}
              {t.lastError ? (
                <ScheduleRow
                  label="Last error"
                  value={<span className="text-destructive text-xs">{t.lastError}</span>}
                />
              ) : null}
              {t.consecutiveFailures > 0 ? (
                <ScheduleRow label="Failures" value={`${String(t.consecutiveFailures)} in a row`} />
              ) : null}
              {t.lastManualRunAt ? (
                <ScheduleRow
                  label="Last manual run"
                  value={formatDistanceToNow(new Date(t.lastManualRunAt), { addSuffix: true })}
                />
              ) : null}
              {t.alertStateChangedAt ? (
                <ScheduleRow
                  label="State since"
                  value={formatDistanceToNow(new Date(t.alertStateChangedAt), { addSuffix: true })}
                />
              ) : null}
              <ScheduleRow
                label="Created"
                value={format(new Date(t.createdAt), 'PPp')}
              />
              <ScheduleRow
                label="Updated"
                value={formatDistanceToNow(new Date(t.updatedAt), { addSuffix: true })}
              />
            </dl>
          </section>
        </aside>
      </div>

      <AlertFormModal isOpen={isEditOpen} task={t} onClose={() => { setIsEditOpen(false); }} />
      <AlertIncidentDisposition
        open={disposition !== null}
        action={disposition ?? 'pause'}
        taskId={t.id}
        taskName={t.name}
        busy={deleteMutation.isPending || disableMutation.isPending}
        onCancel={() => { setDisposition(null); }}
        onConfirm={(resolveIncidents) => {
          if (!disposition) return;
          if (disposition === 'delete') {
            deleteMutation.mutate(
              { taskId: t.id, resolveIncidents },
              {
                onSuccess: () => { void navigate('/alerts'); },
                onSettled: () => { setDisposition(null); },
              },
            );
          } else {
            disableMutation.mutate(
              { taskId: t.id, resolveIncidents },
              { onSettled: () => { setDisposition(null); } },
            );
          }
        }}
      />
    </PageContainer>
  );
}

function ScheduleRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground flex-shrink-0">{label}</dt>
      <dd className="text-right min-w-0 truncate">{value}</dd>
    </div>
  );
}
